import { z } from '@uturn/portal/zod';
import { phoneNumber } from '@uturn/portal/zod/schema';

type AccountPhone = {
	number?: string;
	countryCode?: string;
	comment?: string;
};

/**
 * Parse an account phone through the shared phoneNumber transform, keeping the
 * "empty number → skip" guard and the `countryCode ?? ''` fallback in one place
 * so validation ({@link accountPhoneSchema}) and submission agree on what a
 * valid, normalised phone is. Returns `undefined` when there's no number to
 * validate; otherwise the raw safeParse result for the caller to interpret.
 */
export function parseAccountPhone(phone?: AccountPhone) {
	if (!phone || (phone.number?.trim() ?? '') === '') {
		return undefined;
	}
	return phoneNumber.schema.safeParse({
		...phone,
		countryCode: phone.countryCode ?? '',
	});
}

/**
 * Phone is optional and only its `number` is editable here. `countryCode` is
 * optional at the object level so users with no phone on record (no stored
 * `countryCode`) can leave the field blank without a spurious "required" error
 * — an empty number is treated as "no phone" and skips validation.
 *
 * When a number IS entered it must be a valid phone number, validated through
 * the shared phoneNumber transform (libphonenumber). A missing `countryCode` is
 * passed as `''` so the transform falls back to its default country instead of
 * failing the union before the fallback can run. Issues are re-pathed onto
 * `number` so they render under the phone input.
 */
const accountPhoneSchema = z
	.object({
		number: z.string().optional(),
		countryCode: z.string().optional(),
		comment: z.string().optional(),
	})
	.superRefine((phone, ctx) => {
		const result = parseAccountPhone(phone);
		if (result && !result.success) {
			for (const issue of result.error.issues) {
				ctx.addIssue({ ...issue, path: ['number'] });
			}
		}
	});

/** @see UturnUserRequest */
export const formProfileSchema = z
	.object({
		firstName: z.string().trim().min(1).max(128),
		lastName: z.string().trim().min(1).max(128),
		phoneNumbers: z
			.array(z.object({ phone: accountPhoneSchema }).passthrough())
			.optional(),
	})
	.passthrough();
